/* START OF COMPILED CODE */

import Phaser from 'phaser';
import PlayerCharacter from '../components/player/PlayerCharacter';
import { initInventoryManager, getInventoryManager } from "../managers/InventoryManager";
import { careManager } from '../managers/CareManager';
import { itemManager } from "../managers/ItemManager";

export default class BeachScene extends Phaser.Scene {
    constructor() {
        super({ key: 'Beach' });
        this.debugMode = false; // Debug mode flag

        this.fishing = {
            isFishing: false,
            rod: null,
            line: null,
            castPoint: null,
            fishingZones: null,
            bobber: null,
            biteTimer: null,
            hookTimer: null,
            hasBite: false,
            hooked: false,
            currentCatch: null
        };

        // Add sound management
        this.sounds = {
            splash: null,
            cast: null,
            reeling: null,
            caught1: null,
            caught2: null,
            caught3: null,
            caught4: null,
            lost: null,
            catchSplash: null,
            beach: null
        };

        // Item criteria for fishing in this scene
        this.fishingCriteria = {
            type: 'Consumable',
            subtypePrefix: 'Fish/Saltwater'
        };
    }

    preload() {
        // All assets are pre-loaded by LoadingScene
        // Only load items if needed
        itemManager.preloadAssets(this);
    }

    loadSceneAssets() {
        // Load fishing sprites
        if (!this.textures.exists('fishingRod')) {
            this.load.spritesheet('fishingRod', 
                '/sprites/items/tools/fishingRodCast.png', 
                { frameWidth: 160, frameHeight: 90 }
            );
        }
    
        if (!this.textures.exists('ripple')) {
            this.load.spritesheet('ripple', 
                '/sprites/fishing/ripple.png', 
                { frameWidth: 16, frameHeight: 6 }
            );
        }
        
        if (!this.textures.exists('bobberFloating')) {
            this.load.spritesheet('bobberFloating', 
                '/sprites/fishing/bobberFloating.png', 
                { frameWidth: 16, frameHeight: 24 }
            );
        }
    
        if (!this.textures.exists('bobberBite')) {
            this.load.spritesheet('bobberBite', 
                '/sprites/fishing/bobberBite.png', 
                { frameWidth: 16, frameHeight: 24 }
            );
        }
    
        if (!this.textures.exists('gaugeFrame')) {
            this.load.spritesheet('gaugeFrame', 
                '/sprites/fishing/gaugeFrame.png', 
                { frameWidth: 114, frameHeight: 9 }
            );
        }
    
        if (!this.textures.exists('gaugeFill')) {
            this.load.spritesheet('gaugeFill', 
                '/sprites/fishing/gaugeFill.png', 
                { frameWidth: 114, frameHeight: 9 }
            );
        }
    
        if (!this.textures.exists('minigameFrame')) {
            this.load.spritesheet('minigameFrame', 
                '/sprites/fishing/minigameFrame.png', 
                { frameWidth: 114, frameHeight: 18 }
            );
        }
        
        if (!this.textures.exists('minigameFish')) {
            this.load.spritesheet('minigameFish', 
                '/sprites/fishing/minigameFish.png', 
                { frameWidth: 14, frameHeight: 6 }
            );
        }
    
        if (!this.textures.exists('fishOverlapGreen')) {
            this.load.spritesheet('fishOverlapGreen', 
                '/sprites/fishing/fishOverlapGreen.png', 
                { frameWidth: 16, frameHeight: 8 }
            );
        }
    
        if (!this.textures.exists('fishOverlapYellow')) {
            this.load.spritesheet('fishOverlapYellow', 
                '/sprites/fishing/fishOverlapYellow.png', 
                { frameWidth: 16, frameHeight: 8 }
            );
        }
    
        if (!this.textures.exists('fishOverlapRed')) {
            this.load.spritesheet('fishOverlapRed', 
                '/sprites/fishing/fishOverlapRed.png', 
                { frameWidth: 16, frameHeight: 8 }
            );
        }
        
        // Load scene assets
        if (!this.textures.exists('beach')) {
            this.load.spritesheet('beach', 
                '/sprites/tilesets/beach.png', 
                { frameWidth: 256, frameHeight: 192 });
        }
    
        if (!this.textures.exists('collisionMap')) {
            this.load.image('collisionMap', 'sprites/tilesets/beachCollision.png');
        }
    
        if (!this.textures.exists('fishingCollision')) {
            this.load.image('fishingCollision', 'sprites/tilesets/beachCollision.png');
        }
    
        // Load audio only if not already in cache
        if (!this.cache.audio.exists('beach')) {
            this.load.audio('beach', '/audio/sfx/beach.mp3');
        }
        if (!this.cache.audio.exists('cast')) {
            this.load.audio('cast', '/audio/sfx/fishing/cast.mp3');
        }
        if (!this.cache.audio.exists('reelingStart')) {
            this.load.audio('reelingStart', '/audio/sfx/fishing/reelingStart.mp3');
        }
        if (!this.cache.audio.exists('reeling')) {
            this.load.audio('reeling', '/audio/sfx/fishing/reeling.mp3');
        }
        if (!this.cache.audio.exists('splash')) {
            this.load.audio('splash', '/audio/sfx/fishing/splash.mp3');
        }
        if (!this.cache.audio.exists('catchSplash')) {
            this.load.audio('catchSplash', '/audio/sfx/fishing/catchSplash.mp3');
        }
        if (!this.cache.audio.exists('caught1')) {
            this.load.audio('caught1', '/audio/sfx/fishing/caught1.mp3');
        }
        if (!this.cache.audio.exists('caught2')) {
            this.load.audio('caught2', '/audio/sfx/fishing/caught2.mp3');
        }
        if (!this.cache.audio.exists('caught3')) {
            this.load.audio('caught3', '/audio/sfx/fishing/caught3.mp3');
        }
        if (!this.cache.audio.exists('caught4')) {
            this.load.audio('caught4', '/audio/sfx/fishing/caught4.mp3');
        }
        if (!this.cache.audio.exists('lost')) {
            this.load.audio('lost', '/audio/sfx/fishing/lost.mp3');
        }
    }

    async create(data = {}) {
        initInventoryManager(this);
        
        // Create player animations
        PlayerCharacter.createAnimations(this);

        this.anims.create({
            key: 'beach',
            frames: this.anims.generateFrameNumbers('beach', { start: 0, end: 59 }),
            frameRate: 8,
            repeat: -1
        });



        // Create fishing rod animation
        this.anims.create({
            key: 'rod',
            frames: this.anims.generateFrameNumbers('fishingRod', { start: 0, end: 4 }),
            frameRate: 8,
            repeat: 0
        });
    
        // Create ripple animation without duration
        this.anims.create({
            key: 'ripple',
            frames: this.anims.generateFrameNumbers('ripple', { start: 0, end: 5 }),
            frameRate: 8,
            repeat: -1
        });

        this.anims.create({
            key: 'bobberFloating',
            frames: this.anims.generateFrameNumbers('bobberFloating', { start: 0, end: 3 }),
            frameRate: 4,
            repeat: -1
        });

        this.anims.create({
            key: 'bobberBite',
            frames: this.anims.generateFrameNumbers('bobberBite', { start: 0, end: 3 }),
            frameRate: 8,
            repeat: -1
        });
        
        // Setup camera
        this.cameras.main.setViewport(0, 0, 256, 192);
        
        // Load background with depth
        this.beach = this.add.sprite(128, 96, 'beach').setDepth(0);
        this.beach.play("beach");
        
        // Get user's preferred color and setup player
        const playerColor = await PlayerCharacter.getUserColor(this);
        
        // Define spawn points for different entry locations
        const spawnPoints = {
            default: { x: 15, y: 15 }, // Default spawn
            fromFarm: { x: 240, y: 96 }, // Spawn near farm transition (right side)
            fromRiver: { x: 128, y: 15 }, // Spawn near river transition (top)
            fromMainScene: { x: 128, y: 15 }, // Spawn near main scene transition (top)
        };
        
        // Determine spawn position based on data passed from previous scene
        const spawnKey = data.spawnPoint || 'default';
        const spawnPosition = spawnPoints[spawnKey] || spawnPoints.default;
                
        // Setup player with spawn position
        this.player = new PlayerCharacter(this, spawnPosition.x, spawnPosition.y, playerColor);
        this.player.playColorAnimation("idle2", true);
        
        // Initialize mood dialogue tracking for movement dismissal
        this.activeMoodDialogue = null;
        this.lastPlayerPosition = { x: this.player.x, y: this.player.y };

        // Setup game systems in order
        this.setupFishing();
        await this.setupFishingZones(); 
        this.setupAudio();
        this.setupCollision();
        this.setupSceneTransitions();
        this.setupInputHandlers();
        
        // Setup scene transitions
        this.setupSceneTransitions();
        
        // Create ripples after fishing zones are ready
        this.createRipples();
    }

    setupFishing() {
        // Create sprite with specific frame and scaling
        this.fishing.rod = this.add.sprite(0, 0, 'fishingRod', 0);
        this.fishing.rod.setScale(0.5);
        this.fishing.rod.setVisible(false);
        
        // Create fishing line
        this.fishing.line = this.add.graphics();
    }

    setupFishingZones() {
        return new Promise((resolve) => {
            
            const fishingMap = this.textures.get('fishingCollision').getSourceImage();
            const canvas = document.createElement('canvas');
            canvas.width = fishingMap.width;
            canvas.height = fishingMap.height;
            const context = canvas.getContext('2d');
            context.drawImage(fishingMap, 0, 0);
        
            try {
                const imageData = context.getImageData(0, 0, fishingMap.width, fishingMap.height);
                this.fishingCollisionBodies = this.physics.add.staticGroup();
        
                // Create debug graphics
                const fishingDebugGraphics = this.add.graphics();
                fishingDebugGraphics.lineStyle(1, 0x00ff00, 1); // Green with full opacity
                fishingDebugGraphics.fillStyle(0x00ff00, 0.3); // Semi-transparent fill
        
                let fishingZoneCount = 0;
                let skipRate = 2;
                
                for(let y = 0; y < fishingMap.height; y += skipRate) {
                    for(let x = 0; x < fishingMap.width; x += skipRate) {
                        const idx = (y * fishingMap.width + x) * 4;
                        if(imageData.data[idx + 3] > 0 && 
                           (imageData.data[idx] > 0 || 
                            imageData.data[idx + 1] > 0 || 
                            imageData.data[idx + 2] > 0)) {
                            
                            fishingZoneCount++;
                            
                            // Create a physics-enabled rectangle
                            const rectangle = this.add.rectangle(x, y, skipRate, skipRate, 0x00ff00, 0);
                            this.physics.add.existing(rectangle, true); // true makes it static
                            
                            // Add to static group
                            this.fishingCollisionBodies.add(rectangle);
                            
                        }
                    }
                }
        
                fishingDebugGraphics.setDepth(98); // Just below regular collision debug graphics
                
            } catch (error) {
                console.error('Error processing fishing collision map:', error);
            }
            
            resolve();
        });
    }

    setupAudio() {
        // Stop any existing ambiance
        if (this.sound.get('beach')) {
            this.sound.get('beach').stop();
        }

        // Initialize all sound instances once
        this.sounds.beach = this.sound.add('beach');
        this.sounds.splash = this.sound.add('splash', { volume: 0.4 });
        this.sounds.cast = this.sound.add('cast', { volume: 0.4 });
        this.sounds.reeling = this.sound.add('reeling', { volume: 0.4 });
        this.sounds.reeling.addMarker({
            name: 'middle_section',
            start: 1,
            duration: 1.5,
            config: { loop: true }
        });
        this.sounds.reelingStart = this.sound.add('reelingStart', { volume: 0.4 });
        this.sounds.catchSplash = this.sound.add("catchSplash", { volume: 0.4 });
        this.sounds.caught1 = this.sound.add('caught1', { volume: 0.4 });
        this.sounds.caught2 = this.sound.add('caught2', { volume: 0.4 });
        this.sounds.caught3 = this.sound.add('caught3', { volume: 0.4 });
        this.sounds.caught4 = this.sound.add('caught4', { volume: 0.4 });
        this.sounds.lost = this.sound.add('lost', { volume: 0.4 });

        // Start ambiance
        this.sounds.beach.volume = 0.3;
        this.sounds.beach.loop = true;
        this.sounds.beach.play();
    }

    setupCollision() {
    
        const collisionMap = this.textures.get('collisionMap').getSourceImage();
        const canvas = document.createElement('canvas');
        canvas.width = collisionMap.width;
        canvas.height = collisionMap.height;
        const context = canvas.getContext('2d');
        context.drawImage(collisionMap, 0, 0);
    
        try {
            const imageData = context.getImageData(0, 0, collisionMap.width, collisionMap.height);
            
            // Create static physics group with an existing texture
            this.collisionBodies = this.physics.add.staticGroup();
    

    
            let collisionCount = 0;
            const skipRate = 2;
    
            for(let y = 0; y < collisionMap.height; y += skipRate) {
                for(let x = 0; x < collisionMap.width; x += skipRate) {
                    const idx = (y * collisionMap.width + x) * 4;
                    if(imageData.data[idx] === 255 && 
                       imageData.data[idx + 1] === 0 && 
                       imageData.data[idx + 2] === 0) {
                        
                        collisionCount++;
    
                        // Create a physics-enabled rectangle
                        const rectangle = this.add.rectangle(x, y, skipRate, skipRate, 0xff0000, 0);
                        this.physics.add.existing(rectangle, true); // true makes it static
                        
                        // Add to static group
                        this.collisionBodies.add(rectangle);
                        
                    }
                }
            }
        
            // Make sure player physics is enabled
            if (!this.player.body) {
                this.physics.add.existing(this.player);
            }
    
            // Configure player physics
            this.player.body.setBounce(0);
            this.player.body.setCollideWorldBounds(true);
            
            // Set up collision
            this.physics.add.collider(
                this.player, 
                this.collisionBodies
            );
            
        } catch (error) {
            console.error('Error setting up collision:', error);
        }
    }
    
    setupSceneTransitions() {
        // Create a separate group for scene transition areas
        this.sceneTransitionBodies = this.physics.add.staticGroup();
        
        // Scene transition flag
        this.isTransitioning = false;
        
        // Define scene transition rectangles
        // These use overlap detection (not collision) to trigger scene changes
        const transitionAreas = [
            // Farm scene transition (left edge of beach)
            { x: 1, y: 96, width: 2, height: 64, targetScene: 'Farm' },
            // Add more transitions here as needed:
            // { x: 128, y: 1, width: 64, height: 2, targetScene: 'MainScene' },
        ];
        
        // Create transition rectangles
        transitionAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0x00FF00, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            
            // Store the target scene in the rectangle for easy access
            rectangle.targetScene = area.targetScene;
            
            this.sceneTransitionBodies.add(rectangle);
        });
        
        // Set up overlap detection (not collision) for scene transitions
        this.physics.add.overlap(this.player, this.sceneTransitionBodies, (player, transitionArea) => {
            this.handleSceneTransition(transitionArea.targetScene);
        });
    }
    
    handleSceneTransition(targetScene) {
        // Prevent multiple rapid transitions
        if (this.isTransitioning) return;
        
        this.isTransitioning = true;
                
        // Define spawn point data for the target scene based on current scene
        let sceneData = {};
        if (targetScene === 'Farm') {
            sceneData = { spawnPoint: 'fromBeach' };
        }
        // Add more spawn point mappings here as needed for other scenes
        
        // Optional: Add transition effect (fade out, etc.)
        this.cameras.main.fadeOut(300, 0, 0, 0);
        
        this.cameras.main.once('camerafadeoutcomplete', () => {
            // Clean up before scene change
            this.cleanup();
            
            // Change to the target scene with spawn data
            this.scene.start(targetScene, sceneData);
        });
    }
    
    setupInputHandlers() {
        this.input.keyboard.on('keydown-SPACE', () => {
            if (this.fishing.isFishing) {
                if (this.fishing.hasBite ) {
                    // Player tried to hook during bite window
                    if(!this.fishing.hooked) {
                        this.fishing.hooked = true;
                        this.startMinigame();
                    }
                } else {
                    // If we're already fishing in a valid spot, cancel the fishing
                    if (this.fishing.isFishing) {
                        this.endFishing();
                    }
                }
            } else {
                // Start fishing if we're not already fishing
                this.handleFishing();
            }
        });
    }

    handleFishing() {
        if (this.fishing.isFishing) return;
        
        this.fishing.isFishing = true;
        this.player.setMovementLocked(true);
        
        const rodOffsetX = this.player.flipX ? -20 : 20;
        this.fishing.rod.setPosition(this.player.x + rodOffsetX, this.player.y);
        this.fishing.rod.setFlipX(this.player.flipX);
        this.fishing.rod.setVisible(true);
        this.fishing.rod.setFrame(0);
        
        // Remove any existing animation complete listeners
        this.fishing.rod.removeListener('animationcomplete');
        
        if (this.anims.exists('rod')) {
            // Add new listener and play sounds
            this.fishing.rod.once('animationcomplete', this.handleCastComplete, this);
            this.sounds.cast.play();
            this.fishing.rod.anims.play('rod');
        } else {
            console.error('Rod animation not found');
            this.handleCastComplete();
        }
    }

    handleBite() {
        if (!this.fishing.isFishing) return;
        
        this.fishing.hasBite = true;
        
        // Change bobber animation
        this.fishing.bobber.play('bobberBite');
        
        // Play splash sound
        this.sounds.splash.play();
        
        // Set up hook window timer (2.5 seconds)
        this.fishing.hookTimer = this.time.delayedCall(2500, () => {
            if (!this.fishing.hooked) {  // If still in bite state (player hasn't hooked)
                this.endFishing();  // Fish got away
            }
        }, [], this);
    }

    handleCastComplete() {
        const castDistance = Phaser.Math.Between(35, 70);
        let castX = this.player.x;
        let castY = this.player.y;
        
        const direction = this.player.getLastDirection();
        switch(direction) {
            case 'left':
                castX -= castDistance;
                break;
            case 'right':
                castX += castDistance;
                break;
            case 'up':
                castY -= castDistance;
                break;
            case 'down':
                castY += castDistance;
                break;
        }
        
        this.fishing.castPoint = { x: castX, y: castY };
        
        if (this.fishing.debugGraphics) {
            this.fishing.debugGraphics.destroy();
        }
        this.fishing.debugGraphics = this.add.graphics();
        
        // Draw debug visuals
        if (this.debugMode){
            this.fishing.debugGraphics.lineStyle(2, 0xff0000);
            this.fishing.debugGraphics.strokeCircle(castX, castY, 5);
            this.fishing.debugGraphics.strokeCircle(castX, castY, 2);
            
            this.fishing.debugGraphics.lineStyle(1, 0xff0000);
            this.fishing.debugGraphics.beginPath();
            this.fishing.debugGraphics.moveTo(castX - 8, castY);
            this.fishing.debugGraphics.lineTo(castX + 8, castY);
            this.fishing.debugGraphics.moveTo(castX, castY - 8);
            this.fishing.debugGraphics.lineTo(castX, castY + 8);
            this.fishing.debugGraphics.strokePath();
        }

        this.drawFishingLine();

        const isValid = this.isValidFishingSpot(castX, castY);
        if (isValid) {
            if (this.sounds.splash) {
                this.sounds.splash.stop();
                this.sounds.splash.play();
            }
            this.startFishing();
        } else {
            this.cancelFishing();
        }
    }

    drawFishingLine() {
        if (!this.fishing.castPoint) return;
    
        // Calculate rod tip position
        const rodTipOffsetX = this.player.flipX ? -20 : 22;
        const rodTipOffsetY = 18;
        
        const startX = this.fishing.rod.x + rodTipOffsetX;
        const startY = this.fishing.rod.y + rodTipOffsetY;
    
        // Draw the line
        this.fishing.line.clear();
        this.fishing.line.lineStyle(1, 0xFFFFFF);
        this.fishing.line.beginPath();
        this.fishing.line.moveTo(startX, startY);
        this.fishing.line.lineTo(this.fishing.castPoint.x, this.fishing.castPoint.y);
        this.fishing.line.strokePath();
        
        if (this.debugMode) {
            this.fishing.line.lineStyle(1, 0xFF0000);
            this.fishing.line.strokeCircle(startX, startY, 2);
        }
    }

    isValidFishingSpot(x, y) {
        // Create a temporary physics body at the cast point
        const tempBody = this.add.rectangle(x, y, 2, 2);
        this.physics.add.existing(tempBody);
    
        // Add debug visual for collision check
        const debugGraphics = this.add.graphics();
        debugGraphics.lineStyle(1, 0x00ff00);  // Green outline for collision check area
        debugGraphics.strokeRect(tempBody.x - 1, tempBody.y - 1, 2, 2);
    
        // Do the overlap check
        const overlapping = this.physics.world.overlap(tempBody, this.fishingCollisionBodies);
        
        // Cleanup
        tempBody.destroy();
        debugGraphics.destroy();
        return overlapping;
    }

    startFishing() {
        this.fishing.isFishing = true;
        this.player.setMovementLocked(true);

        // Create and store the bobber in the fishing object
        this.fishing.bobber = this.add.sprite(this.fishing.castPoint.x, this.fishing.castPoint.y, 'bobberFloating');
        this.fishing.bobber.setDepth(1);
        this.fishing.bobber.play('bobberFloating');

        const biteTime = Phaser.Math.Between(1000, 3000);

        // Generate random time until bite (between 5 and 15 seconds)
        // const biteTime = Phaser.Math.Between(5000, 15000);
        
        
        // Set up the bite timer
        this.fishing.biteTimer = this.time.delayedCall(biteTime, () => {
            if (this.fishing.isFishing) {  // Only trigger bite if still fishing
                this.handleBite();
            }
        }, [], this);
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////// MEANINGFUL FISHING GAME CODE HERE ////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////
    startMinigame() {
        this.fishing.currentCatch = itemManager.selectRandomItem(this.fishingCriteria);
        const volatility = itemManager.calculateFishChallengeRating(this.fishing.currentCatch);

        // Center position for our UI elements
        const centerX = this.cameras.main.width / 2;
        
        this.gaugeFrame = this.add.sprite(centerX, 24, 'gaugeFrame', 0);
        this.gaugeFrame.setDepth(1);

        // Create the gauge fill sprite at the center
        this.gaugeFill = this.add.sprite(centerX, 24, 'gaugeFill');
        this.gaugeFill.setDepth(2);

        // Create the mask container starting from the left edge of the gauge
        this.maskContainer = this.add.graphics();
        // Position at the left edge of where the gauge should start
        this.maskContainer.setPosition(centerX - 57, 24 - 4.5); // 57 is half of 114 (gauge width)
        
        // Create the initial mask
        this.updateGaugeMask(10);

        // Create and apply the mask
        const mask = this.maskContainer.createGeometryMask();
        this.gaugeFill.setMask(mask);

        this.minigameFrame = this.add.sprite(this.cameras.main.width / 2, 10, 'minigameFrame', 0);
        this.minigameFrame.setDepth(1);
    
        // Set up initial positions
        const startX = this.cameras.main.width / 2 - 44;
        this.minigameFish = this.add.sprite(startX, 9, 'minigameFish', 0);
        this.minigameFish.setDepth(10);
    
        this.minigameOverlap = this.add.sprite(startX - 44, 9, 'fishOverlapGreen');
        this.minigameOverlap.setDepth(9);
    
        // Set up game state
        this.catchComplete = 10; // Start at 10%
        this.minigame = {
            volatility: volatility,
            fishVelocity: 0.8 * volatility, 
            fishDirection: 1,
            fishPaused: false,
            overlapVelocity: 0,
            lastCheckTime: 0,
            failureTimer: null,
            catchProcessing: false,
            bounds: {
                min: this.cameras.main.width / 2 - 44,
                max: this.cameras.main.width / 2 + 44
            }
        };
    
        // Set up keyboard controls
        this.minigameKeys = {
            A: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
            LEFT: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
            D: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
            RIGHT: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT)
        };
    
        // Create overlap check timer
        this.overlapTimer = this.time.addEvent({
            delay: 100,
            callback: this.checkOverlap,
            callbackScope: this,
            loop: true
        });
    
        // Create fish movement update
        this.fishMovementTimer = this.time.addEvent({
            delay: 16, // ~60fps
            callback: this.updateFishMovement,
            callbackScope: this,
            loop: true
        });
    
        // Create direction change timer based on volatility
        this.directionChangeTimer = this.time.addEvent({
            delay: 3000 / volatility, // Increased base delay
            callback: () => {
                if (this.minigame) {
                    const changeChance = 15 * this.minigame.volatility; // Reduced chance
                    if (Phaser.Math.Between(1, 100) <= changeChance) {
                        this.minigame.fishDirection *= -1;
                        // Reset to base speed in new direction
                        this.minigame.fishVelocity = 0.8 * this.minigame.volatility * this.minigame.fishDirection;
                    }
                }
            },
            loop: true
        });
        
        // Reduced frequency of pauses
        this.pauseTimer = this.time.addEvent({
            delay: 4000 / volatility, // Increased base delay
            callback: () => {
                if (this.minigame && !this.minigame.fishPaused) {
                    const pauseChance = 10 * this.minigame.volatility; // Reduced chance
                    if (Phaser.Math.Between(1, 100) <= pauseChance) {
                        this.minigame.fishPaused = true;
                        this.time.delayedCall(500 / this.minigame.volatility, () => {
                            if (this.minigame) {
                                this.minigame.fishPaused = false;
                                // Reset to base velocity after pause
                                this.minigame.fishVelocity = 0.8 * this.minigame.volatility * this.minigame.fishDirection;
                            }
                        });
                    }
                }
            },
            loop: true
        });
    
        // Play fishing sounds
        const reelingStart = this.sounds.reelingStart;
        const reeling = this.sounds.reeling;
    
        reelingStart.once('complete', () => {
            reeling.play('middle_section');
        });
    
        reelingStart.play();
    }

    updateGaugeMask(percentage) {
        if (!this.maskContainer || !this.gaugeFill) return;
        
        this.maskContainer.clear();
        
        const width = (percentage / 100) * 114;
        
        // Create mask shape without visible fill
        this.maskContainer.beginPath();
        this.maskContainer.fillStyle(0xffffff, 0);  // Alpha 0 makes it invisible
        this.maskContainer.fillRect(0, 0, width, 9);
        
        // Optional: if still seeing the mask, try setting the alpha of the container itself
        this.maskContainer.alpha = 0;
    }
    
    updateFishMovement() {
        if (!this.minigame || !this.minigameFish) return;
    
        // Skip movement if paused
        if (this.minigame.fishPaused) return;
    
        const volatility = this.minigame.volatility;
        
        // Reduced base speed and max velocity scaling
        const baseSpeed = 0.8 * volatility;
        const maxVelocity = 1.5 * volatility;
        
        // Reduced jitter scaling
        const jitter = (Math.random() - 0.5) * (volatility * 0.3);
        
        // Update velocity with reduced jitter
        this.minigame.fishVelocity = (baseSpeed + jitter) * this.minigame.fishDirection;
        
        // Clamp velocity
        this.minigame.fishVelocity = Phaser.Math.Clamp(
            this.minigame.fishVelocity,
            -maxVelocity,
            maxVelocity
        );
    
        // Apply movement within bounds
        const newX = this.minigameFish.x + this.minigame.fishVelocity;
        if (newX >= this.minigame.bounds.min && newX <= this.minigame.bounds.max) {
            this.minigameFish.x = newX;
        } else {
            // Bounce with reduced speed
            this.minigame.fishDirection *= -1;
            this.minigame.fishVelocity = baseSpeed * this.minigame.fishDirection;
        }
    
        // Update overlap movement based on input
        this.updateOverlapMovement();
    }
    
    updateOverlapMovement() {
        if (!this.minigame || !this.minigameOverlap) return;
    
        const acceleration = 0.5;
        const maxVelocity = 6;
        const drag = 0.95;
    
        // Apply input forces
        if (this.minigameKeys.A.isDown || this.minigameKeys.LEFT.isDown) {
            this.minigame.overlapVelocity -= acceleration;
        }
        if (this.minigameKeys.D.isDown || this.minigameKeys.RIGHT.isDown) {
            this.minigame.overlapVelocity += acceleration;
        }
    
        // Apply drag
        this.minigame.overlapVelocity *= drag;
    
        // Clamp velocity
        this.minigame.overlapVelocity = Phaser.Math.Clamp(
            this.minigame.overlapVelocity, 
            -maxVelocity, 
            maxVelocity
        );
    
        // Update position within bounds
        const newX = this.minigameOverlap.x + this.minigame.overlapVelocity;
        this.minigameOverlap.x = Phaser.Math.Clamp(
            newX,
            this.minigame.bounds.min,
            this.minigame.bounds.max
        );
    }
    
    checkOverlap() {
        if (!this.minigame || !this.minigameFish || !this.minigameOverlap) return;
    
        const fishBounds = this.minigameFish.getBounds();
        const overlapBounds = this.minigameOverlap.getBounds();
        const isOverlapping = Phaser.Geom.Rectangle.Overlaps(fishBounds, overlapBounds);
        
        // Check if centers are closely aligned (within 2 pixels)
        const centerAlignment = Math.abs(this.minigameFish.x - this.minigameOverlap.x);
        const perfectOverlap = centerAlignment <= 2;
    
        // Update catch progress
        if (isOverlapping) {
            this.catchComplete += perfectOverlap ? 2 : 1;
        } else {
            this.catchComplete -= 1.2;
        }
    
        // Clamp catch progress
        this.catchComplete = Phaser.Math.Clamp(this.catchComplete, 0, 100);
    
        // Update gauge fill frame
        this.updateGaugeMask(this.catchComplete);
    
        // Check win/loss conditions
        if (this.catchComplete >= 100 && !this.minigame.catchProcessing) {
            this.minigame.catchProcessing = true;  // Set flag to prevent multiple calls
            this.fishCaught();
        } else if (this.catchComplete <= 0) {
            if (!this.minigame.failureTimer) {
                this.minigame.failureTimer = this.time.delayedCall(1000, this.fishLost, [], this);
            }
        } else if (this.minigame.failureTimer) {
            this.minigame.failureTimer.remove();
            this.minigame.failureTimer = null;
        }
    }
    
    async fishCaught() {
        const caughtFish = this.fishing.currentCatch;
        if (!caughtFish) return;
    
        try {
            // Get user data from game registry
            const gameStore = this.game.registry.get('gameStore');
            const { userId } = gameStore;
    
            if (!userId) {
                console.error('No user ID found in game registry');
                return;
            }
    
                        // Get the inventory manager instance
            const inventoryManager = getInventoryManager();
            
            // Add item to inventory using new category system
            // categoryCode 0 = Consumable (as defined in items.json metadata)
            await inventoryManager.addItem({
                itemCode: caughtFish.id,
                categoryCode: 0,
                quantity: 1
            });

            inventoryManager.events.emit('inventory-updated');

            // Play catch sounds and animation
            this.fishCaughtSounds(caughtFish);
            this.player.playColorAnimation('happy5', true);
            
            // Record fishing interaction for care tracking
            careManager.recordInteraction();
    
            // Display catch results with icon
            this.displayCatchResults(caughtFish);
    
            if (this.debugMode) {
                console.log(`Caught a ${caughtFish.rarity} ${caughtFish.name}!`);
                console.log('Added to inventory with itemCode:', caughtFish.id, 'categoryCode:', 0);
            }
    
        } catch (error) {
            console.error('Error processing caught fish:', error);
        } finally {
            this.endFishing();
            // Don't call cleanup() here - it destroys physics groups still in use
            // cleanup() should only be called when the scene is being destroyed
        }
    }
    
    fishCaughtSounds(caughtFish) {
        if (this.sounds.reeling) {
            this.sounds.reeling.stop();
        }
        
        const catchSplash = this.sounds.catchSplash;
        
        // Play splash sound first
        catchSplash.play();
    
        // Play appropriate rarity sound after splash finishes
        catchSplash.once('complete', () => {
            switch (caughtFish.rarity) {
                case 'Legendary':
                    this.sounds.caught4.play();
                    break;
                case 'Epic':
                case 'Rare':
                    this.sounds.caught3.play();
                    break;
                case 'Uncommon':
                    this.sounds.caught2.play();
                    break;
                case 'Normal':
                case 'Junk':
                default:
                    this.sounds.caught1.play();
                    break;
            }
        });
    }
    
    fishLost() {
        // Handle failed catch
        this.sounds.lost.play();
        this.endFishing();
        // Don't call cleanup() here - it destroys physics groups still in use
        // cleanup() should only be called when the scene is being destroyed
    }

    displayCatchResults(fish) {
        // Create a panel to display the catch
        const centerX = this.cameras.main.width / 2;
        const centerY = this.cameras.main.height / 2;
    
        // Create a darkened background panel
        const panel = this.add.rectangle(centerX, centerY, 160, 100, 0x000000)
            .setAlpha(0.8)
            .setDepth(100);
    
        // Get the composed item sprite from ItemManager
        const { sprite: itemSprite } = itemManager.createItemSprite(this, `Consumable_${fish.id}`, centerX, centerY - 20);
        if (itemSprite) {
            itemSprite.setDepth(101);
        }
    
        // Add catch text with rarity-colored name
        const nameText = this.add.text(centerX, centerY + 15, fish.name, {
            fontSize: '16px',
            fill: this.getRarityColor(fish.rarity),
            align: 'center'
        })
            .setOrigin(0.5)
            .setDepth(101);
    
        // Add value text
        const valueText = this.add.text(centerX, centerY + 35, `Value: ${fish.value}g`, {
            fontSize: '12px',
            fill: '#ffffff',
            align: 'center'
        })
            .setOrigin(0.5)
            .setDepth(101);
    
        // Optional: Add particle effects for rare+ fish
        if (['Rare', 'Epic', 'Legendary'].includes(fish.rarity)) {
            this.addCatchParticles(centerX, centerY - 20, fish.rarity);
        }
    
        // Add to inventory if available
        if (window.inventoryManager) {
            window.inventoryManager.addItem({
                itemCode: fish.id,
                categoryCode: 0,
                quantity: 1
            });
        }
    
        // Fade out everything after 2 seconds
        this.time.delayedCall(2000, () => {
            this.tweens.add({
                targets: [panel, itemSprite, nameText, valueText],
                alpha: 0,
                duration: 500,
                onComplete: () => {
                    panel.destroy();
                    if (itemSprite) itemSprite.destroy();
                    nameText.destroy();
                    valueText.destroy();
                }
            });
        });
    }

    addCatchParticles(x, y, rarity) {
        const colors = {
            'Rare': 0x0070dd,
            'Epic': 0xa335ee,
            'Legendary': 0xff8000
        };
    
        const particles = this.add.particles(x, y, {
            speed: { min: 50, max: 150 },
            angle: { min: 0, max: 360 },
            scale: { start: 0.5, end: 0 },
            blendMode: 'ADD',
            lifespan: 1000,
            gravityY: 100,
            tint: colors[rarity],
            quantity: 20
        });
    
        this.time.delayedCall(1000, () => {
            particles.destroy();
        });
    }
    
    getRarityColor(rarity) {
        const colors = {
            'Junk': '#9d9d9d',
            'Normal': '#ffffff',
            'Uncommon': '#1eff00',
            'Rare': '#0070dd',
            'Epic': '#a335ee',
            'Legendary': '#ff8000',
            'Quest': '#ffff00'
        };
        return colors[rarity] || '#ffffff';
    }
    

    endFishing() {
        // Clean up all fishing-related visuals and states
        this.fishing.rod.setVisible(false);
        this.fishing.line.clear();

        if (this.sounds.reelingStart) {
            this.sounds.reelingStart.stop();
        }
        if (this.sounds.reeling) {
            this.sounds.reeling.stop();
        }

        if (this.fishing.debugGraphics) {
            this.fishing.debugGraphics.destroy();
        }
        
        // Destroy the bobber if it exists
        if (this.fishing.bobber) {
            this.fishing.bobber.destroy();
        }

        // Clear timers if they exist
        if (this.fishing.biteTimer) {
            this.fishing.biteTimer.remove();
            this.fishing.biteTimer = null;
        }
        if (this.fishing.hookTimer) {
            this.fishing.hookTimer.remove();
            this.fishing.hookTimer = null;
        }

        // Reset fishing state
        this.fishing.isFishing = false;
        this.fishing.hasBite = false;
        this.fishing.hooked = false;
        this.fishing.castPoint = null;
        
        this.time.delayedCall(1000, () => {
            this.player.playColorAnimation("idle2", true);
            this.player.setMovementLocked(false);
        }, [], this);
    }

    cancelFishing() {
        // Only clear if we're not at a valid fishing spot
        if (!this.fishing.castPoint || !this.isValidFishingSpot(this.fishing.castPoint.x, this.fishing.castPoint.y)) {
            this.endFishing();
        }
    }

    createRipples() {
        const maxAttempts = 50;
        const numRipples = 3;
        const ripples = [];
        const rippleWidth = 16;
        const rippleHeight = 6;
        
        let attempts = 0;
        while (ripples.length < numRipples && attempts < maxAttempts) {
            const x = Phaser.Math.Between(rippleWidth/2, this.cameras.main.width - rippleWidth/2);
            const y = Phaser.Math.Between(rippleHeight/2, this.cameras.main.height - rippleHeight/2);
            
            if (this.isValidRippleBounds(x, y, rippleWidth, rippleHeight)) {
                const tooClose = ripples.some(ripple => {
                    return Phaser.Math.Distance.Between(x, y, ripple.x, ripple.y) < rippleWidth;
                });
                
                if (!tooClose) {
                    const ripple = this.add.sprite(x, y, 'ripple', 0);
                    ripple.setDepth(1);
                    ripple.setDisplaySize(rippleWidth, rippleHeight);
                    
                    let currentFrame = 0;
                    const frameCount = 6;
                    
                    this.time.addEvent({
                        delay: 125,
                        callback: () => {
                            if (ripple && ripple.active) {
                                currentFrame = (currentFrame + 1) % frameCount;
                                ripple.setFrame(currentFrame);
                            }
                        },
                        loop: true
                    });
                    
                    ripples.push(ripple);
                }
            }
            attempts++;
        }
        
        this.activeRipples = ripples;
    }

    isValidRippleBounds(centerX, centerY, width, height) {
        const checkPoints = [
            { x: centerX - width/2, y: centerY - height/2 }, // Top-left
            { x: centerX + width/2, y: centerY - height/2 }, // Top-right
            { x: centerX - width/2, y: centerY + height/2 }, // Bottom-left
            { x: centerX + width/2, y: centerY + height/2 }, // Bottom-right
            { x: centerX, y: centerY - height/2 }, // Top middle
            { x: centerX, y: centerY + height/2 }, // Bottom middle
            { x: centerX - width/2, y: centerY }, // Left middle
            { x: centerX + width/2, y: centerY }, // Right middle
            { x: centerX, y: centerY } // Center
        ];
        
        for (const point of checkPoints) {
            return this.isValidFishingSpot(point.x, point.y);
        }
        
        return true;
    }

    update() {
        if (this.player && this.player.update) {
            this.player.update();
            
            // Check for player movement to dismiss mood dialogues
            this.checkPlayerMovementForMoodDialogue();
        }
    }
    
    checkPlayerMovementForMoodDialogue() {
        if (!this.player || !this.activeMoodDialogue) {
            return;
        }
        
        const currentPos = { x: this.player.x, y: this.player.y };
        const threshold = 2; // Minimum distance to consider "movement"
        
        const distanceMoved = Phaser.Math.Distance.Between(
            this.lastPlayerPosition.x, 
            this.lastPlayerPosition.y, 
            currentPos.x, 
            currentPos.y
        );
        
        if (distanceMoved > threshold) {
            // Player has moved, dismiss mood dialogue
            this.activeMoodDialogue.destroy();
            this.activeMoodDialogue = null;
        }
        
        // Update last position
        this.lastPlayerPosition.x = currentPos.x;
        this.lastPlayerPosition.y = currentPos.y;
    }

    cleanup() {
        // Clean up all minigame timers
        if (this.overlapTimer) this.overlapTimer.remove();
        if (this.fishMovementTimer) this.fishMovementTimer.remove();
        if (this.directionChangeTimer) this.directionChangeTimer.remove();
        if (this.pauseTimer) this.pauseTimer.remove();
        if (this.minigame?.failureTimer) this.minigame.failureTimer.remove();
        if (this.minigame?.unpauseTimer) this.minigame.unpauseTimer.remove();
        if (this.minigame?.failureTimer) this.minigame.failureTimer.remove();

    
        // Clean up sound instances
        if (this.sounds.reeling) this.sounds.reeling.stop();
        if (this.sounds.reelingStart) this.sounds.reelingStart.stop();


        // Clean up animation listeners
        if (this.fishing.rod) this.fishing.rod.removeListener('animationcomplete');

    
        // Destroy sprites
        if (this.gaugeFrame) this.gaugeFrame.destroy();
        if (this.gaugeFill) this.gaugeFill.destroy();
        if (this.minigameFrame) this.minigameFrame.destroy();
        if (this.minigameFish) this.minigameFish.destroy();
        if (this.minigameOverlap) this.minigameOverlap.destroy();
        if (this.gaugeMask) this.gaugeMask.destroy();
        if (this.gaugeFill?.mask) {
            this.gaugeFill.mask.destroy();
            this.gaugeFill.clearMask();
        }

        // Ensure player movement is unlocked during cleanup
        if (this.player) this.player.setMovementLocked(false);

        // Clean up collision bodies
        if (this.collisionBodies) {
            this.collisionBodies.destroy();
        }
        
        // Clean up fishing collision bodies
        if (this.fishingCollisionBodies) {
            this.fishingCollisionBodies.destroy();
        }
        
        // Clean up scene transition bodies
        if (this.sceneTransitionBodies) {
            this.sceneTransitionBodies.destroy();
        }

        // Reset minigame state
        this.minigame = null;
        this.minigameKeys = null;
    }
}

/* END OF COMPILED CODE */